import React, { useEffect } from 'react';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import useTimeline from '@src/hooks/useTimeline';
import useAudioWaveform from '@src/hooks/useAudioWaveform';
import Waveform from '@src/new-components/Timeline/Waveform';
import { THUMB_STATUS } from '@src/constants/timeline';
import { COLOR } from '@src/styles/color';
import {
  StyledMusicClip,
  StyledMusicLabel,
  StyledTrimBar,
  StyledTrimHandle,
  StyledWaveformFill,
  StyledWaveformSlot,
} from './index.style';

// Music clips are short, so a lighter waveform than the wide media lane.
// `reload`: the picker plays each track through an <audio> with no crossOrigin,
// which makes S3 cache a copy our CORS fetch can't read — a fresh fetch avoids
// it (same as the voice track). No duration cap: the check for it would hit that
// same bad cache, and the byte cap already limits the download.
const MUSIC_WAVEFORM_OPTS = {
  peaksPerSecond: 8,
  minPeaks: 60,
  maxPeaks: 600,
  reload: true,
};

/**
 * One music clip: a name label with its audio waveform below. Each clip decodes
 * its own url. When `selected` it shows trim handles on both edges. Presentational
 * — the parent handles position, drag/menu, selection and trimming.
 */
const MusicClip = ({
  clip,
  style,
  dragging,
  selected = false,
  trimStart = 0,
  onPointerDown,
  onContextMenu,
  onHandlePointerDown,
  onDuration,
  isLocked = false,
  isPlaying = false,
}) => {
  const { secToPx } = useTimeline();
  const { status, peaks, duration } = useAudioWaveform(
    clip.link,
    MUSIC_WAVEFORM_OPTS,
  );

  // Report the measured song length up so the parent can cap trimming at it.
  useEffect(() => {
    if (duration) onDuration?.(clip.id, duration);
  }, [duration, clip.id, onDuration]);

  // Draw the whole song at the timeline's px-per-second, shifted left by
  // `trimStart` so the in-point is at the clip's left edge. The slot's width then
  // clips the skipped intro and the cut tail.
  const pxPerSec = secToPx(1) - secToPx(0);
  const waveformWidthPx = duration ? duration * pxPerSec : 0;
  const waveformShiftPx = trimStart * pxPerSec;

  // A running export or project playback both make the clip read-only: no
  // select, move, trim or menu. Mirrors the voice clip's `isInert` — including
  // that only `isLocked` drops pointer-events, so a press while playing is
  // swallowed here instead of falling through to the timeline's seek surface.
  const isInert = isLocked || isPlaying;

  return (
    <StyledMusicClip
      type="button"
      // Owns its own gesture (select / move / trim) — opts the clip out of the
      // timeline's click-anywhere-to-seek surface, see services/isSeekSurface.
      data-timeline-clip
      dragging={dragging}
      selected={selected}
      isLocked={isLocked}
      isPlaying={isPlaying}
      aria-disabled={isInert}
      style={style}
      onPointerDown={isInert ? undefined : onPointerDown}
      onContextMenu={isInert ? undefined : onContextMenu}
    >
      {selected && !isInert && (
        <StyledTrimHandle
          side="left"
          onPointerDown={(event) =>
            onHandlePointerDown?.(event, clip.id, 'start')
          }
        >
          <StyledTrimBar />
        </StyledTrimHandle>
      )}
      <StyledMusicLabel>
        <MusicNoteRoundedIcon />
        <span>{clip.name}</span>
      </StyledMusicLabel>
      {status === THUMB_STATUS.READY && waveformWidthPx > 0 && (
        <StyledWaveformSlot>
          <StyledWaveformFill
            style={{
              width: waveformWidthPx,
              transform: `translateX(${-waveformShiftPx}px)`,
            }}
          >
            <Waveform peaks={peaks} color={COLOR.bluev2[48]} />
          </StyledWaveformFill>
        </StyledWaveformSlot>
      )}
      {selected && !isInert && (
        <StyledTrimHandle
          side="right"
          onPointerDown={(event) =>
            onHandlePointerDown?.(event, clip.id, 'end')
          }
        >
          <StyledTrimBar />
        </StyledTrimHandle>
      )}
    </StyledMusicClip>
  );
};

export default MusicClip;
